import { defaultEmojiDataResolver } from "frimousse"
import { useEffect, useSyncExternalStore } from "react"
import { catalogOf, type EmojiCatalog } from "./catalog.ts"
import { emojibaseUrl } from "./hosting.ts"

/*
 * The standard emoji's catalog, loaded once a page first wants it and kept for the session. The
 * emoji are the picker's own data (frimousse's reading of Emojibase, cached in the browser by it),
 * so a shortcode and the picker always give the same emoji; the shortcodes ship with the web app as
 * a chunk of their own. A load that fails is tried again by the next page that wants it.
 */

let catalog: EmojiCatalog | undefined
let loading: Promise<EmojiCatalog> | undefined
const listeners = new Set<() => void>()

export function loadEmojiCatalog(): Promise<EmojiCatalog> {
  loading ??= Promise.all([
    defaultEmojiDataResolver("en", { emojibaseUrl }),
    import("emojibase-data/en/shortcodes/iamcal.json"),
  ]).then(
    ([data, shortcodes]) => {
      catalog = catalogOf(data.emojis, shortcodes.default)
      for (const listener of listeners) listener()
      return catalog
    },
    (error: unknown) => {
      loading = undefined
      throw error
    },
  )
  return loading
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * The catalog, or none until it has loaded. `wanted` starts loading it; a page that only might need
 * it (a message that may hold a shortcode) passes whether it does.
 */
export function useEmojiCatalog(wanted = true): EmojiCatalog | undefined {
  const current = useSyncExternalStore(subscribe, () => catalog)
  useEffect(() => {
    if (wanted && !catalog) loadEmojiCatalog().catch(() => undefined)
  }, [wanted])
  return current
}
